/**
 * The turn's files, as a table (ADR-22 decision 5).
 *
 * The prose is where files are presented; this is the secondary view — collapsed by default,
 * and expanded *above* the action row so the response's controls stay its last element. It is a
 * table rather than tiles because what the gallery could not show is exactly what matters now:
 * the description its author wrote, beside the file's own name and size.
 *
 * Four columns and the actions, deliberately: revision and provenance are on the card, where
 * there is room to explain them, and in a table they were two mostly-empty columns pushing the
 * description — the column people actually read — off a phone.
 *
 * Rows come from `latestRevisions` like every other files surface, folded by the caller — a
 * second fold beside it is how the gallery ended up listing four tiles for two deliverables.
 */

import { ActionIcon, Box, Group, Table, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconDownload, IconEye } from '@tabler/icons-react';
import { useTrackArtifactDownload } from '~portal/contexts';
import { downloadFile } from '~portal/lib';
import { useArtifactSignedUrl } from '~portal/stores/artifact-store';
import { formatFileSize, getFormatIcon } from './artifact-format';
import { filesTable } from './ArtifactFilesTable.css';
import { useArtifactViewer } from './ArtifactViewer';

/** @desc One deliverable, as the table shows it. Resolved by the caller from the turn's items. */
export type ArtifactFileRow = {
  artifactId: string;
  /** @desc `<name>.<format>`, or the id-based fallback for artefacts made before names existed. */
  name: string;
  description?: string;
  format: string;
  revision: number;
  /**
   * @desc `<name>.<format>` of the artefact this was built from, when the pointer resolves.
   * Carried for the card and the viewer; the table has no provenance column.
   */
  derivedFromLabel?: string;
  fileSize?: number;
  isImage: boolean;
};

export function ArtifactFilesTable({ rows }: { rows: readonly ArtifactFileRow[] }) {
  if (rows.length === 0) return null;

  return (
    <Box className={filesTable.wrapper} mt="xs">
      <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm" fz="xs">
        <Table.Thead className={filesTable.head}>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Format</Table.Th>
            <Table.Th>Size</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <FileRow key={row.artifactId} row={row} />
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

function FileRow({ row }: { row: ArtifactFileRow }) {
  const { url } = useArtifactSignedUrl(row.artifactId);
  const { open, standalone } = useArtifactViewer();
  const trackDownload = useTrackArtifactDownload();

  const download = () => {
    if (!url) return;
    trackDownload(row.format, 'table');
    downloadFile(url, row.name);
  };

  /** The row's own action, and the chip's and the card's: preview an image, download the rest. */
  const activate = () => {
    if (row.isImage) open(row.artifactId);
    else download();
  };

  return (
    <Table.Tr>
      <Table.Td>
        <UnstyledButton className={filesTable.name} onClick={activate}>
          <Box component="span" className={filesTable.icon}>
            {getFormatIcon(row.format, 16)}
          </Box>
          <Text size="xs" fw={500}>
            {row.name}
          </Text>
        </UnstyledButton>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          {row.description ?? '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed" tt="uppercase">
          {row.format}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          {formatFileSize(row.fileSize) ?? '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap={2} wrap="nowrap" justify="flex-end">
          {row.isImage && (
            <Tooltip label="Preview" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label={`Preview ${row.name}`}
                onClick={() => open(row.artifactId)}
              >
                <IconEye size={15} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Download" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={`Download ${row.name}`}
              data-dd-action-name="Download file"
              disabled={!url}
              onClick={download}
            >
              <IconDownload size={15} />
            </ActionIcon>
          </Tooltip>
        </Group>
        {standalone}
      </Table.Td>
    </Table.Tr>
  );
}
